import React, { useMemo } from 'react';
import { Button, List, Space, Tag, Typography } from 'antd';
import {
  RightOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  FileTextOutlined,
  TeamOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import type { HrmsDashboardActionSummary } from '@/services/hrmsDashboard.service';

const { Text } = Typography;

type Severity = 'critical' | 'attention' | 'info';

interface AttentionItem {
  id: string;
  severity: Severity;
  title: string;
  reason: string;
  count: number;
  action_label: string;
  section: string;
  owner_hint?: string;
}

const severityColor: Record<Severity, string> = {
  critical: 'red',
  attention: 'orange',
  info: 'blue',
};

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const HrmsAttentionBrief: React.FC<{
  summary: HrmsDashboardActionSummary;
}> = ({ summary }) => {
  const items = useMemo(() => {
    const out: AttentionItem[] = [];
    const expired = summary.expired_docs_count ?? 0;
    const dueSoon = summary.docs_due_soon_count ?? 0;
    const overdueOnb = summary.talent?.overdue_onboarding_tasks ?? 0;
    const leave = summary.pending_leave_count ?? 0;
    const ot = summary.pending_ot_count ?? 0;
    const contracts = summary.contracts_ending_count ?? 0;
    const confirm = summary.confirmation_due_count ?? 0;
    const loans = summary.overdue_loan_installment_count ?? 0;
    const payrollBlockers =
      (summary.unapproved_timesheet_count ?? 0) +
      (summary.draft_adjustment_count ?? 0) +
      (summary.payroll_period_status &&
      !['Closed', 'Paid', 'Approved'].includes(summary.payroll_period_status)
        ? 1
        : 0);
    const joiners = summary.joiners_this_week_count ?? 0;
    const leavers = summary.leavers_this_week_count ?? 0;

    if (expired > 0) {
      out.push({
        id: 'expired-docs',
        severity: 'critical',
        title: 'Expired immigration / employee documents',
        reason: 'Passport, visa, EID or labour card already past expiry — renew before compliance risk.',
        count: expired,
        action_label: 'Follow up',
        section: 'hrms-follow-up',
        owner_hint: 'HR / PRO',
      });
    }
    if (overdueOnb > 0 && summary.talent?.enabled) {
      out.push({
        id: 'onboarding-overdue',
        severity: 'critical',
        title: 'Overdue onboarding tasks',
        reason: 'Day-1 tasks past due — chase owners before join friction.',
        count: overdueOnb,
        action_label: 'Onboarding',
        section: 'hrms-follow-up',
        owner_hint: 'Talent / HR',
      });
    }
    if (confirm > 0) {
      out.push({
        id: 'confirmation-due',
        severity: 'attention',
        title: 'Confirmations overdue',
        reason: 'Probation ended and confirmation still Pending — confirm or extend.',
        count: confirm,
        action_label: 'Confirm',
        section: 'hrms-follow-up',
        owner_hint: 'HR / Managers',
      });
    }
    if (dueSoon > 0) {
      out.push({
        id: 'docs-due-soon',
        severity: 'attention',
        title: 'Documents due within 30 days',
        reason: 'Renewals needed soon — start PRO / embassy process early.',
        count: dueSoon,
        action_label: 'Queue',
        section: 'hrms-follow-up',
        owner_hint: 'HR / PRO',
      });
    }
    if (leave > 0) {
      out.push({
        id: 'pending-leave',
        severity: 'attention',
        title: 'Leave awaiting approval',
        reason: 'Pending / PendingApproval leave applications blocking planning.',
        count: leave,
        action_label: 'Approve',
        section: 'hrms-follow-up',
        owner_hint: 'Managers',
      });
    }
    if (ot > 0) {
      out.push({
        id: 'pending-ot',
        severity: 'attention',
        title: 'OT awaiting approval',
        reason: 'Overtime requests need approval before payroll cut-off.',
        count: ot,
        action_label: 'Approve',
        section: 'hrms-follow-up',
        owner_hint: 'Managers',
      });
    }
    if (contracts > 0) {
      out.push({
        id: 'contracts-ending',
        severity: 'attention',
        title: 'Contracts ending within 30 days',
        reason: 'Renew or convert limited contracts before they lapse.',
        count: contracts,
        action_label: 'Contracts',
        section: 'hrms-follow-up',
        owner_hint: 'HR',
      });
    }
    if (loans > 0) {
      out.push({
        id: 'loan-overdue',
        severity: 'attention',
        title: 'Overdue loan installments',
        reason: 'Schedule periods past due still Pending — recover via payroll or chase.',
        count: loans,
        action_label: 'Loans',
        section: 'payroll-readiness',
        owner_hint: 'Payroll',
      });
    }
    if (summary.payroll_enabled && payrollBlockers > 0) {
      out.push({
        id: 'payroll-blockers',
        severity: 'attention',
        title: 'Payroll readiness blockers',
        reason: 'Unapproved timesheets, draft adjustments, or open payroll period need attention.',
        count: payrollBlockers,
        action_label: 'Payroll',
        section: 'payroll-readiness',
        owner_hint: 'Payroll',
      });
    }
    if (joiners + leavers > 0) {
      out.push({
        id: 'movements',
        severity: 'info',
        title: 'Joiners / leavers this week',
        reason: 'Workforce movement — prepare assets, access, and exit clearance.',
        count: joiners + leavers,
        action_label: 'Workforce',
        section: 'workforce-approvals',
        owner_hint: 'HR',
      });
    }
    return out;
  }, [summary]);

  const highlights = useMemo(() => {
    const tags: string[] = [];
    if (summary.active_headcount > 0) tags.push(`${summary.active_headcount} active`);
    if (summary.expired_docs_count > 0) tags.push(`${summary.expired_docs_count} expired docs`);
    if (summary.pending_leave_count > 0) tags.push(`${summary.pending_leave_count} leave`);
    if (summary.confirmation_due_count > 0) tags.push(`${summary.confirmation_due_count} confirm`);
    if (summary.talent?.enabled && summary.talent.overdue_onboarding_tasks > 0) {
      tags.push(`${summary.talent.overdue_onboarding_tasks} onboarding`);
    }
    return tags;
  }, [summary]);

  const iconFor = (id: string) => {
    if (id.includes('doc')) return <IdcardOutlined />;
    if (id.includes('leave') || id.includes('ot')) return <ClockCircleOutlined />;
    if (id.includes('confirm') || id.includes('contract')) return <ExceptionOutlined />;
    if (id.includes('payroll') || id.includes('loan')) return <FileTextOutlined />;
    if (id.includes('movements') || id.includes('onboarding')) return <TeamOutlined />;
    return <WarningOutlined />;
  };

  return (
    <ControlTowerSectionCard
      zone="attention"
      sectionId="attention-brief"
      title={
        <Space>
          <ThunderboltOutlined />
          Attention Brief
        </Space>
      }
    >
      {highlights.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {highlights.map((h) => (
            <Tag key={h} color="geekblue">
              {h}
            </Tag>
          ))}
        </Space>
      )}
      {!items.length ? (
        <EmptyQueueState message="No urgent HR compliance, leave, or payroll items right now." />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => scrollTo(a.section)}
                >
                  {a.action_label}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={iconFor(a.id)}
                title={
                  <Space wrap>
                    <Tag color={severityColor[a.severity]}>{a.severity}</Tag>
                    <Text strong>{a.title}</Text>
                    <Tag>{a.count}</Tag>
                  </Space>
                }
                description={
                  <Text type="secondary">
                    Why: {a.reason}
                    {a.owner_hint ? ` · ${a.owner_hint}` : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </ControlTowerSectionCard>
  );
};

export default HrmsAttentionBrief;
